<template>
  <router-view />
</template>

<script lang="ts">
import { computed, defineComponent, inject, provide, type Ref, watch } from 'vue';
import { PageStore } from 'stores/Page/Page.ts';
import { storeToRefs } from 'pinia';
import { LocalStorage, useMeta, useQuasar } from 'quasar';
import { AuthStore } from 'stores/User/Auth.ts';
import { pagesInSafe } from 'src/router';
import { useRoute } from 'vue-router';

declare module 'lodash';

const parseMetaTags = (htmlString) => {
  if (!htmlString) {
    return {};
  }
  const metaObject = {};
  const regex = /<meta\s+([^>]+)>/g;
  const matches = htmlString.matchAll(regex);

  for (const match of matches) {
    const attrs = match[1];
    const charsetMatch = attrs.match(/charset=["']?([^"'\s>]+)["']?/);
    const httpEquivMatch = attrs.match(/http-equiv=["']?([^"'\s>]+)["']?/);
    const contentMatch = attrs.match(/content=["']?([^"'\s>]+)["']?/);

    if (charsetMatch) {
      metaObject['charset'] = { charset: charsetMatch[1] };
    } else if (httpEquivMatch && contentMatch) {
      metaObject[httpEquivMatch[1].toLowerCase()] = {
        'http-equiv': httpEquivMatch[1],
        content: contentMatch[1],
      };
    }
  }

  return metaObject;
};

export default defineComponent({
  name: 'App',

  async preFetch({ store, currentRoute }) {
    const pageStore = PageStore(store);
    await pageStore.getPage(currentRoute.path, currentRoute.query);
  },

  provide() {
    return {
      me: this.me,
      siteLang: this.siteLang || 'tr-TR',
      siteAddress: this.siteAddress || '',
      siteBreadcrumbs: this.siteBreadcrumbs || [],
      siteContent: this.siteContent,
      currentRoute: this.currentRoute,
    };
  },

  setup() {
    const $q = useQuasar();
    const $route = useRoute();
    const pageStore = PageStore();

    const { head, header, content, breadcrumbs, currentRoute } = storeToRefs(pageStore);

    const authStore = AuthStore();
    const { isLogged, me } = storeToRefs(authStore);

    provide(
      'isLogged',
      computed(() => isLogged.value)
    );

    const metaTags: {
      [name: string]: {
        name: string;
        content: string;
      };
    } = {};
    const headMeta = head.value.meta;
    const socialMeta = head.value.socialMeta;
    const openGraph = head.value.openGraph;
    const headInfo = head.value.info;

    const excludeMetas = ['metaFields'];
    for (const name of Object.keys(headMeta)) {
      if (headMeta[name] && !excludeMetas.includes(name)) {
        metaTags[name] = { name: name, content: headMeta[name] };
      }
    }

    for (const name of Object.keys(socialMeta)) {
      if (socialMeta[name]) {
        metaTags[name] = { name: name, content: socialMeta[name] };
      }
    }

    for (const name of Object.keys(openGraph)) {
      if (openGraph[name]) {
        metaTags[name] = { property: name, content: openGraph[name] };
      }
    }

    metaTags.canonical.content += $route.path;
    metaTags.description = {
      name: 'description',
      content: head.value.title.description,
    };

    const cleanSiteAddress = (head.value.title.siteAddress || '').replace(/\/+$/, '');

    useMeta({
      title: head.value.title.siteTitle,
      meta: {
        ...metaTags,
        ...parseMetaTags(headMeta.metaFields),
      },

      script: {
        googleTag: {
          async: 'async',
          src: 'https://www.googletagmanager.com/gtag/js?id=G-J2J8KFMFNR',
        },
        googleAnalytics: {
          type: 'application/javascript',
          innerHTML: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());

            gtag('config', 'G-J2J8KFMFNR');
        `,
        },
        schemaSearch: {
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            url: cleanSiteAddress,
            name: head.value.title.siteTitle,
            description: head.value.title.description,
            potentialAction: {
              '@type': 'SearchAction',
              target: `${cleanSiteAddress}/s?q={search_term_string}`,
              'query-input': 'required name=search_term_string',
            },
          }),
        },
        schemaOrganization: {
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: head.value.title.siteTitle,
            url: cleanSiteAddress,
            logo: head.value.title.siteLogo,
            sameAs: Object.keys(header.value.socialInfo || {})
              .map((o) => header.value.socialInfo[o].link)
              .filter((o) => o && o !== '#'),
          }),
        },
        schemaNavigation: {
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            itemListElement: Object.values(header.value.mainNav || {}).map((nav: any, index: number) => ({
              '@type': 'SiteNavigationElement',
              position: index + 1,
              name: nav.name,
              url: `${cleanSiteAddress}/${(nav.link || '').replace(/^\/+/, '')}`,
            })),
          }),
        },
      },
    });

    return {
      meta: {
        title: head.value.title.siteTitle,
        titleTemplate: (title) => `${title}`,
      },
      isLogged: computed(() => isLogged.value),
      me: computed(() => me),
      authStore,
      siteLang: headInfo.lang,
      siteAddress: cleanSiteAddress,
      siteBreadcrumbs: breadcrumbs,
      siteContent: content,
      currentRoute,

      showCookieNotify() {
        $q.notify({
          message: 'Sitemizden en iyi şekilde faydalanabilmeniz için çerezler kullanılmaktadır. Bu siteye giriş yaparak çerez kullanımını kabul etmiş sayılıyorsunuz.',
          color: 'accent',
          textColor: 'primary',
          timeout: 0,
          multiLine: true,
          avatar: '/img/cute-cookie.png',
          actions: [
            {
              label: 'Daha fazla bilgi',
              color: 'primary',
              handler: () => {
                window.location.replace('/cerez-politikasi');
              },
            },
            {
              label: 'Tamam',
              color: 'primary',
              handler: () => {
                LocalStorage.set('cookie-policy', 1);
              },
            },
          ],
        });
      },
    };
  },

  created() {
    if (!LocalStorage.getItem('cookie-policy')) {
      this.showCookieNotify();
    }
  },

  mounted() {
    this.authStore.checkToken();
    this.authStore.parseToken(this.authStore.token);
  },

  watch: {
    $route: {
      handler() {
        this.authStore.checkToken();
      },
      immediate: true,
    },
    isLogged: {
      handler(newValue) {
        if (!newValue) {
          this.$nextTick(() => {
            pagesInSafe.forEach((page) => {
              if (this.$route.name === page) {
                window.location.replace('/');
              }
            });
          });
        }
      },
      deep: true,
    },
  },
});
</script>
